import React, { useState } from "react";
import { useApp } from "../../context/AppContext";
import { is2FAEnabled, verify2FA } from "../../utils/auth2fa";
import { isTOTPEnabled, verifyTOTP } from "../../utils/totp2fa";
import { Lock, Eye, EyeOff, Shield, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, loginError } = useApp();
  const [code, setCode] = useState("");
  const [show, setShow] = useState(false);
  
  // 2FA states
  const [step, setStep] = useState("code"); // "code", "pin", "totp"
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState("");
  const [pendingCode, setPendingCode] = useState("");

  const handleCodeSubmit = (e) => {
    if (e) e.preventDefault();
    const c = code.trim();
    if (!c) return;

    // Check Google Authenticator first
    if (isTOTPEnabled(c)) {
      setPendingCode(c);
      setStep("totp");
      setTotpCode("");
      setTotpError("");
      return;
    }

    // Check PIN 2FA
    if (is2FAEnabled(c)) {
      setPendingCode(c);
      setStep("pin");
      setPin("");
      setPinError("");
      return;
    }

    // No 2FA — login directly
    login(c);
  };

  const handlePinVerify = (e) => {
    if (e) e.preventDefault();
    if (pin.length < 4) return;
    const result = verify2FA(pendingCode, pin);
    if (result.success) {
      login(pendingCode);
      resetAll();
    } else {
      setPinError(result.error);
      setPin("");
      if (result.locked) {
        setTimeout(() => resetAll(), 2000);
      }
    }
  };

  const handleTotpVerify = (e) => {
    if (e) e.preventDefault();
    if (totpCode.length !== 6) return;
    if (verifyTOTP(pendingCode, totpCode)) {
      login(pendingCode);
      resetAll();
    } else {
      setTotpError("Invalid code. Check your Authenticator app.");
      setTotpCode("");
    }
  };

  const resetAll = () => {
    setStep("code");
    setPin("");
    setPinError("");
    setTotpCode("");
    setTotpError("");
    setPendingCode("");
    setCode("");
  };

  // ============================
  // GOOGLE AUTHENTICATOR SCREEN
  // ============================
  if (step === "totp") {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#060911 0%,#0F172A 50%,#060911 100%)"}}>
        <div style={{width:420,padding:44,borderRadius:24,background:"rgba(15,23,42,.8)",border:"1px solid rgba(66,133,244,.15)",backdropFilter:"blur(24px)",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.5)"}}>
          
          <div style={{width:68,height:68,borderRadius:18,background:"linear-gradient(135deg,#4285F4,#34A853)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:32,boxShadow:"0 8px 32px rgba(66,133,244,.25)"}}>📱</div>
          
          <h2 style={{color:"#E2E8F0",fontSize:22,fontWeight:800,marginBottom:4}}>Google Authenticator</h2>
          <p style={{color:"#64748B",fontSize:13,margin:"4px 0 28px"}}>Enter the 6-digit code from your Authenticator app</p>
          
          {totpError && <div style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.25)",borderRadius:12,padding:"12px 16px",marginBottom:20,color:"#EF4444",fontSize:13,fontWeight:600}}>{totpError}</div>}
          
          <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:24}}>
            {[0,1,2,3,4,5].map(i => (
              <div key={i} style={{width:44,height:54,borderRadius:10,border:totpCode.length>i?"2px solid #4285F4":"2px solid rgba(100,116,139,.25)",background:totpCode.length>i?"rgba(66,133,244,.06)":"rgba(15,23,42,.4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:800,color:"#E2E8F0",transition:"all .15s"}}>
                {totpCode[i] || ""}
              </div>
            ))}
          </div>
          
          <input type="text" inputMode="numeric" maxLength={6} value={totpCode} onChange={e=>{setTotpCode(e.target.value.replace(/\D/g,""));setTotpError("");}} onKeyDown={e=>{if(e.key==="Enter"&&totpCode.length===6)handleTotpVerify(e);}} autoFocus style={{width:"100%",padding:"14px 20px",borderRadius:14,border:"1px solid rgba(66,133,244,.2)",background:"rgba(15,23,42,.5)",color:"#E2E8F0",fontSize:24,textAlign:"center",letterSpacing:10,fontWeight:800,outline:"none",marginBottom:20,boxSizing:"border-box"}} placeholder="000000" />
          
          <button onClick={handleTotpVerify} disabled={totpCode.length!==6} style={{width:"100%",padding:"15px 0",borderRadius:14,border:"none",background:totpCode.length===6?"linear-gradient(135deg,#4285F4,#34A853)":"rgba(100,116,139,.15)",color:totpCode.length===6?"#fff":"#475569",fontSize:15,fontWeight:800,cursor:totpCode.length===6?"pointer":"not-allowed",marginBottom:16,boxShadow:totpCode.length===6?"0 4px 20px rgba(66,133,244,.3)":"none"}}>
            Verify & Login
          </button>
          
          <button onClick={resetAll} style={{background:"none",border:"none",color:"#64748B",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6,margin:"0 auto"}}>
            <ArrowLeft size={14} /> Back to Access Code
          </button>
          
          <div style={{marginTop:20,fontSize:11,color:"#334155"}}>Code refreshes every 30 seconds</div>
        </div>
      </div>
    );
  }

  // ============================
  // PIN 2FA SCREEN
  // ============================
  if (step === "pin") {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#060911 0%,#0F172A 50%,#060911 100%)"}}>
        <div style={{width:420,padding:44,borderRadius:24,background:"rgba(15,23,42,.8)",border:"1px solid rgba(245,166,35,.12)",backdropFilter:"blur(24px)",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.5)"}}>
          
          <div style={{width:68,height:68,borderRadius:18,background:"linear-gradient(135deg,#F5A623,#E8960F)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",boxShadow:"0 8px 32px rgba(245,166,35,.25)"}}>
            <Shield size={32} color="#000" />
          </div>
          
          <h2 style={{color:"#E2E8F0",fontSize:22,fontWeight:800,marginBottom:4}}>Security Verification</h2>
          <p style={{color:"#64748B",fontSize:13,margin:"4px 0 28px"}}>Enter your 2FA PIN to continue</p>
          
          {pinError && <div style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.25)",borderRadius:12,padding:"12px 16px",marginBottom:20,color:"#EF4444",fontSize:13,fontWeight:600}}>{pinError}</div>}
          
          <div style={{display:"flex",justifyContent:"center",gap:10,marginBottom:24}}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{width:50,height:58,borderRadius:12,border:pin.length>i?"2px solid #F5A623":"2px solid rgba(100,116,139,.25)",background:pin.length>i?"rgba(245,166,35,.06)":"rgba(15,23,42,.4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:800,color:"#E2E8F0",transition:"all .2s"}}>
                {pin[i] ? "●" : ""}
              </div>
            ))}
          </div>
          
          <input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e=>{setPin(e.target.value.replace(/\D/g,""));setPinError("");}} onKeyDown={e=>{if(e.key==="Enter"&&pin.length>=4)handlePinVerify(e);}} autoFocus style={{width:"100%",padding:"14px 20px",borderRadius:14,border:"1px solid rgba(245,166,35,.2)",background:"rgba(15,23,42,.5)",color:"#E2E8F0",fontSize:22,textAlign:"center",letterSpacing:12,fontWeight:700,outline:"none",marginBottom:20,boxSizing:"border-box"}} placeholder="● ● ● ●" />
          
          <button onClick={handlePinVerify} disabled={pin.length<4} style={{width:"100%",padding:"15px 0",borderRadius:14,border:"none",background:pin.length>=4?"linear-gradient(135deg,#F5A623,#E8960F)":"rgba(100,116,139,.15)",color:pin.length>=4?"#000":"#475569",fontSize:15,fontWeight:800,cursor:pin.length>=4?"pointer":"not-allowed",marginBottom:16,boxShadow:pin.length>=4?"0 4px 20px rgba(245,166,35,.3)":"none"}}>
            Verify & Login
          </button>
          
          <button onClick={resetAll} style={{background:"none",border:"none",color:"#64748B",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6,margin:"0 auto"}}>
            <ArrowLeft size={14} /> Back to Access Code
          </button>
          
          <div style={{marginTop:20,fontSize:11,color:"#334155"}}>🔒 3 wrong attempts = 5 min lockout</div>
        </div>
      </div>
    );
  }

  // ============================
  // ACCESS CODE SCREEN
  // ============================
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#060911 0%,#0F172A 50%,#060911 100%)"}}>
      <div style={{width:420,padding:44,borderRadius:24,background:"rgba(15,23,42,.8)",border:"1px solid rgba(100,116,139,.12)",backdropFilter:"blur(24px)",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.5)"}}>
        
        <div style={{width:68,height:68,borderRadius:18,background:"linear-gradient(135deg,#F5A623,#E8960F)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:24,fontWeight:900,color:"#000",boxShadow:"0 8px 32px rgba(245,166,35,.25)"}}>FB</div>
        
        <h1 style={{fontSize:28,fontWeight:900,margin:"0 0 4px",letterSpacing:"-1px"}}>
          <span style={{color:"#E2E8F0"}}>Fin</span><span style={{color:"#F5A623"}}>Board</span>
        </h1>
        <p style={{color:"#64748B",fontSize:13,margin:"0 0 32px"}}>Stake Affiliate Financial Command Center</p>
        
        {loginError && <div style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.25)",borderRadius:12,padding:"12px 16px",marginBottom:20,color:"#EF4444",fontSize:13,fontWeight:600}}>{loginError}</div>}
        
        <div style={{textAlign:"left",fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:1.2,marginBottom:8}}>Access Code</div>
        
        <div style={{position:"relative",marginBottom:20}}>
          <div style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",color:"#475569"}}><Lock size={18} /></div>
          <input type={show?"text":"password"} value={code} onChange={e=>setCode(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")handleCodeSubmit(e);}} placeholder="Enter your access code" autoFocus style={{width:"100%",padding:"14px 48px 14px 46px",borderRadius:14,border:"1px solid rgba(245,166,35,.2)",background:"rgba(15,23,42,.5)",color:"#E2E8F0",fontSize:15,outline:"none",boxSizing:"border-box"}} />
          <button onClick={()=>setShow(!show)} style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#475569",cursor:"pointer",padding:4}}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        
        <button onClick={handleCodeSubmit} style={{width:"100%",padding:"15px 0",borderRadius:14,border:"none",background:"linear-gradient(135deg,#F5A623,#E8960F)",color:"#000",fontSize:15,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 20px rgba(245,166,35,.3)"}}>
          Unlock Dashboard
        </button>
        
        <div style={{marginTop:24,display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:11,color:"#334155"}}>
          <Shield size={12} /> AES-256-GCM Encrypted | SHA-256 Auth
        </div>
      </div>
    </div>
  );
}
